import { getIosReviewAccessHeaderValue } from './iosReviewAccess';
import { isIosNativeAppRuntime } from './runtimeCapabilities';

export const NATIVE_ACCOUNT_CHANGED_EVENT = 'zia:native-account-changed';

const DEFAULT_NATIVE_API_ORIGIN = 'https://ziawellnav.com';
const API_PATH_PATTERN = /^\/api(?:[/?#]|$)/;
const NATIVE_LOCAL_PROTOCOLS = new Set(['capacitor:', 'ionic:']);
const NATIVE_IOS_INSTALL_ID_KEY = 'ziawellnav_ios_install_id';
const NATIVE_IOS_LINKED_USER_KEY = 'ziawellnav_ios_linked_user';
const NATIVE_IOS_CLIENT = 'ziawellnav-ios';
const PUBLIC_NATIVE_GET_HEADER_BYPASS_PATTERNS = [
  /^\/api\/config(?:[/?#]|$)/,
  /^\/api\/facilities\/search(?:[/?#]|$)/,
  /^\/api\/wells\/search(?:[/?#]|$)/,
];

type NativeApiRouteOptions = {
  apiOrigin?: string;
  forceNative?: boolean;
};

export type NativeIosAuthHeaders = {
  'X-ZiaWellNav-Runtime': 'ios-native-app';
  'X-ZiaWellNav-Client': typeof NATIVE_IOS_CLIENT;
  'X-ZiaWellNav-IOS-Install-ID': string;
  'X-ZiaWellNav-Native-Session'?: string;
  'X-ZiaWellNav-Review-Access'?: string;
};

export type NativeIosLinkedUser = {
  userId: string;
  username?: string | null;
  companyId?: string | null;
  /** Human company name for UI — never show companyId UUID to end users. */
  companyName?: string | null;
  sessionToken?: string | null;
  linkedAt?: string;
};

const UUID_LIKE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** True when a string looks like an opaque company id (not a display name). */
export function looksLikeCompanyId(value: string | null | undefined): boolean {
  const text = String(value || '').trim();
  if (!text) return false;
  if (UUID_LIKE.test(text)) return true;
  // Short opaque ids sometimes used in tests/fixtures.
  if (/^[0-9a-f]{16,}$/i.test(text) && !/\s/.test(text)) return true;
  return false;
}

/**
 * End-user label for the linked company. Never returns a raw companyId UUID.
 */
export function getNativeIosLinkedCompanyDisplayName(
  user: Pick<NativeIosLinkedUser, 'companyId' | 'companyName'> | null | undefined,
  fallback: string | null = null,
): string | null {
  const name = String(user?.companyName || '').trim();
  if (name && !looksLikeCompanyId(name)) return name;
  // Never surface companyId / UUIDs in customer-facing copy.
  return fallback;
}

declare global {
  interface Window {
    __ziaNativeApiRoutingInstalled?: boolean;
    __ziaNativeBeaconRoutingInstalled?: boolean;
  }
}

export function getNativeApiOrigin(): string {
  const configured = String((import.meta as any).env?.VITE_API_BASE_URL || DEFAULT_NATIVE_API_ORIGIN).trim();
  try {
    return new URL(configured || DEFAULT_NATIVE_API_ORIGIN).origin;
  } catch {
    return DEFAULT_NATIVE_API_ORIGIN;
  }
}

function shouldRouteNativeApi(options: NativeApiRouteOptions = {}): boolean {
  return options.forceNative === true || isIosNativeAppRuntime();
}

function createNativeInstallId(): string {
  const cryptoApi = typeof crypto !== 'undefined' ? crypto : null;
  if (typeof cryptoApi?.randomUUID === 'function') return cryptoApi.randomUUID();
  const random = Math.random().toString(36).slice(2, 12);
  return `ios-${Date.now().toString(36)}-${random}`;
}

export function getNativeIosInstallId(): string {
  if (typeof window === 'undefined') return createNativeInstallId();

  try {
    const existing = window.localStorage?.getItem(NATIVE_IOS_INSTALL_ID_KEY);
    if (existing) return existing;
    const created = createNativeInstallId();
    window.localStorage?.setItem(NATIVE_IOS_INSTALL_ID_KEY, created);
    return created;
  } catch {
    return createNativeInstallId();
  }
}

export function getNativeIosLinkedUser(): NativeIosLinkedUser | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage?.getItem(NATIVE_IOS_LINKED_USER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<NativeIosLinkedUser>;
    const userId = String(parsed.userId || '').trim();
    if (!userId) return null;
    const companyNameRaw = parsed.companyName ? String(parsed.companyName).trim() : '';
    const companyName = companyNameRaw && !looksLikeCompanyId(companyNameRaw) ? companyNameRaw : null;
    return {
      userId,
      username: parsed.username ? String(parsed.username) : null,
      companyId: parsed.companyId ? String(parsed.companyId) : null,
      companyName,
      sessionToken: parsed.sessionToken ? String(parsed.sessionToken) : null,
      linkedAt: parsed.linkedAt ? String(parsed.linkedAt) : undefined,
    };
  } catch {
    return null;
  }
}

export function setNativeIosLinkedUser(user: NativeIosLinkedUser): void {
  if (typeof window === 'undefined') return;
  const userId = String(user.userId || '').trim();
  if (!userId) return;

  try {
    const existing = getNativeIosLinkedUser();
    const companyId = user.companyId ? String(user.companyId) : null;
    const incomingName = user.companyName ? String(user.companyName).trim() : '';
    const sameCompany = Boolean(
      companyId && existing?.companyId && companyId === existing.companyId,
    );
    const companyName = incomingName && !looksLikeCompanyId(incomingName)
      ? incomingName
      : (sameCompany ? (existing?.companyName || null) : null);
    window.localStorage?.setItem(NATIVE_IOS_LINKED_USER_KEY, JSON.stringify({
      userId,
      username: user.username || null,
      companyId,
      companyName,
      sessionToken: user.sessionToken || null,
      linkedAt: user.linkedAt || new Date().toISOString(),
    }));
    window.dispatchEvent(new Event(NATIVE_ACCOUNT_CHANGED_EVENT));
  } catch {
    // Native account linking should not break API routing if storage is full.
  }
}

export function clearNativeIosLinkedUser(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage?.removeItem(NATIVE_IOS_LINKED_USER_KEY);
    window.dispatchEvent(new Event(NATIVE_ACCOUNT_CHANGED_EVENT));
  } catch {
    // Best effort cleanup only.
  }
}

export function getNativeIosAuthHeaders(options: NativeApiRouteOptions = {}): NativeIosAuthHeaders | null {
  if (!shouldRouteNativeApi(options)) return null;
  const linkedUser = getNativeIosLinkedUser();
  const headers: NativeIosAuthHeaders = {
    'X-ZiaWellNav-Runtime': 'ios-native-app',
    'X-ZiaWellNav-Client': NATIVE_IOS_CLIENT,
    'X-ZiaWellNav-IOS-Install-ID': getNativeIosInstallId(),
  };
  if (linkedUser?.sessionToken) headers['X-ZiaWellNav-Native-Session'] = linkedUser.sessionToken;
  const reviewCode = getIosReviewAccessHeaderValue();
  if (reviewCode) headers['X-ZiaWellNav-Review-Access'] = reviewCode;
  return headers;
}

export function resolveNativeApiUrl(rawUrl: string, options: NativeApiRouteOptions = {}): string {
  if (!shouldRouteNativeApi(options)) return rawUrl;

  const apiOrigin = options.apiOrigin || getNativeApiOrigin();
  if (API_PATH_PATTERN.test(rawUrl)) return `${apiOrigin}${rawUrl}`;

  try {
    const parsed = new URL(rawUrl);
    const isLocalHost = parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1';
    const isCapacitorLocal = NATIVE_LOCAL_PROTOCOLS.has(parsed.protocol) && isLocalHost;
    // Some screens still hardcode http://localhost:8788 when hostname === "localhost".
    // Capacitor WebView also reports that hostname — rewrite those API calls to prod.
    const isDevWranglerLocal =
      (parsed.protocol === 'http:' || parsed.protocol === 'https:') &&
      isLocalHost &&
      (parsed.port === '8788' || parsed.port === '8787' || parsed.port === '');
    if ((isCapacitorLocal || isDevWranglerLocal) && API_PATH_PATTERN.test(parsed.pathname)) {
      return `${apiOrigin}${parsed.pathname}${parsed.search}${parsed.hash}`;
    }
  } catch {
    // Non-URL strings that are not /api paths are left untouched.
  }

  return rawUrl;
}

export function resolveNativeApiRequestInfo(input: RequestInfo | URL, options: NativeApiRouteOptions = {}): RequestInfo | URL {
  if (typeof input === 'string') return resolveNativeApiUrl(input, options);

  if (input instanceof URL) {
    const routed = resolveNativeApiUrl(input.toString(), options);
    return routed === input.toString() ? input : new URL(routed);
  }

  if (typeof Request !== 'undefined' && input instanceof Request) {
    const routed = resolveNativeApiUrl(input.url, options);
    if (routed === input.url) return input;

    const method = input.method || 'GET';
    const hasForwardedBody = method !== 'GET' && method !== 'HEAD' && input.body != null;
    return new Request(routed, {
      method,
      headers: input.headers,
      body: hasForwardedBody ? (input.body as BodyInit | null) : undefined,
      mode: input.mode,
      credentials: input.credentials,
      cache: input.cache,
      redirect: input.redirect,
      referrer: input.referrer,
      referrerPolicy: input.referrerPolicy,
      integrity: input.integrity,
      keepalive: input.keepalive,
      signal: input.signal,
      ...(hasForwardedBody ? { duplex: 'half' } : {}),
    } as RequestInit & { duplex?: 'half' });
  }

  return input;
}

function isApiRequestTarget(input: RequestInfo | URL, options: NativeApiRouteOptions = {}): boolean {
  if (!shouldRouteNativeApi(options)) return false;
  const apiOrigin = options.apiOrigin || getNativeApiOrigin();
  const rawUrl = typeof input === 'string'
    ? input
    : input instanceof URL
      ? input.toString()
      : typeof Request !== 'undefined' && input instanceof Request
        ? input.url
        : '';

  if (API_PATH_PATTERN.test(rawUrl)) return true;

  try {
    const parsed = new URL(rawUrl);
    return parsed.origin === apiOrigin && API_PATH_PATTERN.test(parsed.pathname);
  } catch {
    return false;
  }
}

function mergeHeaders(input: RequestInfo | URL, init?: RequestInit): Headers {
  const headers = new Headers(
    typeof Request !== 'undefined' && input instanceof Request
      ? input.headers
      : undefined,
  );
  if (init?.headers) {
    new Headers(init.headers).forEach((value, key) => headers.set(key, value));
  }
  return headers;
}

function getFetchMethod(input: RequestInfo | URL, init?: RequestInit): string {
  if (init?.method) return init.method.toUpperCase();
  if (typeof Request !== 'undefined' && input instanceof Request) return (input.method || 'GET').toUpperCase();
  return 'GET';
}

function getRequestPathname(input: RequestInfo | URL): string {
  const rawUrl = typeof input === 'string'
    ? input
    : input instanceof URL
      ? input.toString()
      : typeof Request !== 'undefined' && input instanceof Request
        ? input.url
        : '';

  if (API_PATH_PATTERN.test(rawUrl)) return rawUrl.split(/[?#]/, 1)[0] || rawUrl;

  try {
    return new URL(rawUrl).pathname;
  } catch {
    return '';
  }
}

function shouldBypassNativeIosHeaders(input: RequestInfo | URL, init?: RequestInit): boolean {
  if (getFetchMethod(input, init) !== 'GET') return false;
  const pathname = getRequestPathname(input);
  return PUBLIC_NATIVE_GET_HEADER_BYPASS_PATTERNS.some((pattern) => pattern.test(pathname));
}

export function resolveNativeApiFetchArgs(
  input: RequestInfo | URL,
  init?: RequestInit,
  options: NativeApiRouteOptions = {},
): [RequestInfo | URL, RequestInit | undefined] {
  const routedInput = resolveNativeApiRequestInfo(input, options);
  if (!isApiRequestTarget(routedInput, options)) return [routedInput, init];
  if (shouldBypassNativeIosHeaders(routedInput, init)) return [routedInput, init];

  const nativeHeaders = getNativeIosAuthHeaders(options);
  if (!nativeHeaders) return [routedInput, init];

  const headers = mergeHeaders(routedInput, init);
  Object.entries(nativeHeaders).forEach(([key, value]) => {
    if (!headers.has(key)) headers.set(key, value);
  });

  return [routedInput, { ...init, headers }];
}

export function installNativeApiRouting(): void {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;

  if (!window.__ziaNativeApiRoutingInstalled) {
    const baseFetch = window.fetch.bind(window);
    window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
      const [routedInput, routedInit] = resolveNativeApiFetchArgs(input, init);
      return baseFetch(routedInput, routedInit);
    }) as typeof window.fetch;
    window.__ziaNativeApiRoutingInstalled = true;
  }

  if (!window.__ziaNativeBeaconRoutingInstalled && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    const baseSendBeacon = navigator.sendBeacon.bind(navigator);
    navigator.sendBeacon = ((url: string | URL, data?: BodyInit | null) => {
      const routed = resolveNativeApiRequestInfo(url) as string | URL;
      return baseSendBeacon(routed, data);
    }) as typeof navigator.sendBeacon;
    window.__ziaNativeBeaconRoutingInstalled = true;
  }
}
